import React, { useState, ReactElement, useMemo } from "react";
import { keyBy } from "lodash";
import { useParams, Link, useLocation } from "wouter";

import { Button } from "../../../../../components/src/Button";
import { Grid, Column } from "../../../../../components/src";
import { Dropdown } from "../../../../../components/src/Dropdown";

import { YesNoModal } from "../../../../../components/src/YesNoModal";
import { useMutation } from "urql";
import { DELETE_PARTY_ACCOUNT } from "../../../../../graphql/src/party/queries";
import { PartyPageData } from "../PartyPage";
import { PartyAccountForm } from "../PartyAccountForm";
import { Markdown } from "../../../../../components/src/Markdown";

export const PartyAccountsTab = ({ data, refetch }: { data: PartyPageData; refetch: () => void }): ReactElement => {
    const { id: partyId, itemId } = useParams<{ id: string; tabId: string; itemId: string }>();
    const [, navigate] = useLocation();

    const [modal, setModal] = useState({ showModal: false, payload: null });

    const [_, deletePartyAccount] = useMutation(DELETE_PARTY_ACCOUNT);

    const { selectedAccount, accountsById } = useMemo(() => {
        let selectedAccount = null;
        let accountsById = null;
        if (itemId && data?.party && data?.party.accounts) {
            selectedAccount = data.party.accounts.find((a) => a._id.toString() === itemId.toString());
        }

        accountsById = data && data.party && data.party.accounts ? keyBy(data.party.accounts, "_id") : null;

        return { selectedAccount, accountsById };
    }, [data, itemId]);

    const { treeExist, md } = useMemo(() => {
        let md = `~~~mermaid
    graph TB\n`;
        let treeExist = false;
        if (data && data.party && data.party.accounts) {
            data.party.accounts.forEach((account) => {
                if (account.parentAccountId !== "000000000000000000000000") {
                    const parent = accountsById[account.parentAccountId];
                    if (parent) {
                        treeExist = true;
                        const parentDescription = parent.description || "parent.description";
                        const accountDescription = account.description || "account.description";
                        md =
                            md +
                            account.parentAccountId +
                            "(" +
                            parentDescription +
                            ")-->" +
                            account._id +
                            "(" +
                            accountDescription +
                            ") \n";
                    }
                }
            });
        }

        md = md + "~~~\n";
        return { treeExist, md };
    }, [data, accountsById]);

    const selectAccount = (accountId): void => {
        navigate(`/parties/${partyId}/accounts/${accountId}`, { replace: true });
    };

    if (!data) return <div></div>;

    return (
        <div>
            {modal.showModal ? (
                <YesNoModal
                    warningText={"Are you sure you want to delete account with id " + modal.payload.accountId + "?"}
                    modal={{
                        showModal: modal.showModal,
                        payload: modal.payload
                    }}
                    setModal={setModal}
                    onYes={() => {
                        deletePartyAccount({ partyId: modal.payload.partyId, accountId: modal.payload.accountId })
                            .then(() => {
                                refetch();
                                navigate(`/parties/${partyId}/accounts`);
                            })
                            .catch((error) => {
                                console.error(error);
                            });
                    }}
                />
            ) : null}
            <div id="partyaccountsform" className="partyaccountsform form mx-3">
                <div className="w-full mb-4">
                    {treeExist ? (
                        <div className="w-full block" style={{ minHeight: "300px" }}>
                            <div className="w-full [&_.markdown]:block [&_.markdown]:w-full">
                                <Markdown key={`/parties/${partyId}/accounts/${itemId}`} markdown={md} />
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="flex flex-wrap mb-5">Account is a transaction grouper, like a portfolio.</div>
                <Button
                    className="mt-2"
                    variant="success"
                    size="sm"
                    onClick={() => {
                        navigate(`/parties/${partyId}/accounts/new`);
                    }}
                >
                    Add account
                </Button>

                <div className="flex flex-wrap -mx-2 mb-5">
                    <div className={itemId ? "w-full lg:w-7/12 px-2 mb-4" : "w-full px-2 mb-4"}>
                        {data && data.party ? (
                            <div>
                                <Grid data={data.party.accounts} sortable tableClassName="table-xs">
                                    <Column
                                        field="_id"
                                        format={(field) => {
                                            return (
                                                <a
                                                    href="#selectAccount"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        selectAccount(field);
                                                    }}
                                                >
                                                    {field}
                                                </a>
                                            );
                                        }}
                                    />
                                    <Column field="name" className="grid-column-sticky" />
                                    <Column field="type" />
                                    <Column field="description" />
                                    <Column
                                        field="_id"
                                        title=""
                                        format={(value) => {
                                            return (
                                                <Dropdown>
                                                    <Dropdown.Toggle variant="danger" size="sm">
                                                        Delete
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu>
                                                        <Dropdown.Item
                                                            as="button"
                                                            onClick={async () => {
                                                                setModal({
                                                                    showModal: true,
                                                                    payload: { accountId: value, partyId: partyId }
                                                                });
                                                            }}
                                                        >
                                                            Delete account
                                                        </Dropdown.Item>
                                                        <Dropdown.Item>Cancel</Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            );
                                        }}
                                    />
                                </Grid>
                            </div>
                        ) : null}
                    </div>
                </div>
                <div className="flex flex-wrap -mx-2">
                    {itemId && data && data.party ? (
                        <div className="w-full lg:w-5/12 px-2 mb-4">
                            <Link className="close-btn float-right" to={`/parties/${partyId}/accounts`} />
                            <PartyAccountForm
                                key={itemId}
                                partyId={data.party._id}
                                account={selectedAccount}
                                externalAccounts={data.party.externalAccounts}
                                accounts={data.party.accounts.map((account) => ({
                                    _id: account._id,
                                    name: account.name,
                                    description: account.description || "",
                                    type: account.type
                                }))}
                                partyInstruments={data.party.instruments.map((instrument) => ({
                                    _id: instrument._id,
                                    name: instrument.name || ""
                                }))}
                                instruments={data.instruments.map((instrument) => ({
                                    _id: instrument._id,
                                    name: instrument.name || ""
                                }))}
                                onCreate={() => {
                                    refetch();
                                    navigate(`/parties/${partyId}/accounts`);
                                }}
                                onUpdate={() => {
                                    refetch();
                                }}
                            />
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
